import { useState } from "react";
import {
  Loader2, AlertCircle, RefreshCw, Search, Check, X, GraduationCap,
  ThumbsUp, Undo2, Building2, Clock,
} from "lucide-react";
import {
  useGetHodApplicationsQuery,
  useDecideHodApplicationMutation,
  useBulkApproveHodApplicationsMutation,
  type HodApplication,
} from "../api/hodPledgeApi";

/**
 * Where a Head of Department confirms their own final-year students.
 *
 * This replaces Section E of the paper form. The HOD is answering one
 * question per student — is this a bona fide final-year student of my
 * department, expected to graduate as stated — so the screen shows exactly
 * what is needed to answer it and nothing else. The student's phone number
 * and career plans are the Institute's business, not the department's.
 *
 * Most HODs will open this a handful of times a year, from an email, having
 * never seen the portal before. So: no jargon, the question stated in full
 * at the top, and the common case (confirm everyone) reachable in one press.
 */

const TABS = [
  { key: "pending", label: "Awaiting you" },
  { key: "approved", label: "Confirmed" },
  { key: "declined", label: "Declined" },
  { key: "", label: "All" },
];

function fmt(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso.replace(" ", "T")).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

export default function HodPledgeApprovalsPage() {
  const [tab, setTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<number[]>([]);
  const [declining, setDeclining] = useState<HodApplication | null>(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");

  const { data, isLoading, isFetching, refetch } = useGetHodApplicationsQuery({
    ...(tab ? { status: tab } : {}),
    ...(q ? { q } : {}),
  });

  const [decide, { isLoading: deciding }] = useDecideHodApplicationMutation();
  const [bulkApprove, { isLoading: bulking }] = useBulkApproveHodApplicationsMutation();

  const rows = data?.data ?? [];
  const meta = data?.meta ?? ({} as any);
  const totals = meta?.totals ?? { pending: 0, approved: 0, declined: 0 };
  const institutions = meta?.institutions ?? [];

  const pendingIds = rows.filter((r) => r.status === "pending").map((r) => r.id);
  const allSelected = pendingIds.length > 0 && pendingIds.every((id) => selected.includes(id));

  const approve = async (row: HodApplication) => {
    setError(""); setNote("");
    try {
      const res = await decide({ id: row.id, status: "approved" }).unwrap();
      setNote(res?.message ?? "Confirmed.");
    } catch (e: any) {
      setError(e?.data?.message || "Could not save that.");
    }
  };

  // The server scopes this to the caller's own departments, so a stale
  // selection cannot reach anyone else's students.
  const approveSelected = async () => {
    setError(""); setNote("");
    try {
      const res = await bulkApprove(selected).unwrap();
      setNote(res?.message ?? "Confirmed.");
      setSelected([]);
    } catch (e: any) {
      setError(e?.data?.message || "Could not confirm those.");
    }
  };

  // Nobody is the HOD of anything — a plain explanation beats an empty table.
  if (!isLoading && institutions.length === 0) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-sm">
        <div className="max-w-lg mx-auto text-center py-10 space-y-3">
          <Building2 className="w-10 h-10 text-slate-300 mx-auto" />
          <h2 className="text-lg font-bold text-slate-800">No department linked to your account</h2>
          <p className="text-sm text-slate-500 leading-relaxed">
            {meta?.message
              || "You are not currently recorded as the Head of Department for any institution on the NIQS register."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-5 bg-white rounded-lg shadow-sm">

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Graduate Pledge approvals</h2>
          <p className="text-xs text-gray-500 font-medium mt-1 max-w-2xl">
            Final-year students of{" "}
            <strong>{institutions.map((i: any) => i.name).join(", ")}</strong> who have completed
            the NIQS Declaration of Intent. You are confirming one thing: that each is a bona fide
            final-year student of your department, expected to graduate as stated.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="p-2.5 rounded-lg border text-gray-500 hover:bg-gray-50 disabled:opacity-40 self-start"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}
      {note && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-emerald-200 bg-emerald-50 text-sm text-emerald-800">
          <Check className="w-4 h-4 mt-0.5 shrink-0" /><span>{note}</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex gap-2 overflow-x-auto pb-1 flex-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setSelected([]); }}
              className={`px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap border ${
                tab === t.key
                  ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                  : "border-slate-200 text-slate-500 hover:bg-slate-50"
              }`}
            >
              {t.label}
              {t.key && (
                <span className="ml-1.5 font-black">
                  {(totals as any)[t.key] ?? 0}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="relative sm:w-64">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
            placeholder="Name or matric no.…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") setQ(search.trim()); }}
            onBlur={() => setQ(search.trim())}
          />
        </div>
      </div>

      {/* Confirming a whole cohort is one routine act, so it is one press. */}
      {tab === "pending" && pendingIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-slate-50 px-4 py-3">
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              className="accent-indigo-600 w-4 h-4"
              checked={allSelected}
              onChange={(e) => setSelected(e.target.checked ? pendingIds : [])}
            />
            Select all {pendingIds.length} awaiting you
          </label>
          <div className="flex-1" />
          <button
            onClick={approveSelected}
            disabled={selected.length === 0 || bulking}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-40"
          >
            {bulking ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsUp className="w-4 h-4" />}
            Confirm {selected.length || ""} selected
          </button>
        </div>
      )}

      <div className="overflow-x-auto border rounded-xl">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              {tab === "pending" && <th className="w-10 px-3 py-2.5" />}
              <th className="text-left px-3 py-2.5 font-black">Student</th>
              <th className="text-left px-3 py-2.5 font-black">Matric no.</th>
              <th className="text-left px-3 py-2.5 font-black">Programme</th>
              <th className="text-left px-3 py-2.5 font-black">Expected graduation</th>
              <th className="text-left px-3 py-2.5 font-black">Applied</th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading && (
              <tr><td colSpan={7} className="px-3 py-10 text-center">
                <Loader2 className="w-5 h-5 text-indigo-400 animate-spin mx-auto" />
              </td></tr>
            )}

            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-sm text-slate-400">
                {tab === "pending" ? "Nothing is waiting on you." : "Nothing here."}
              </td></tr>
            )}

            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50">
                {tab === "pending" && (
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      className="accent-indigo-600 w-4 h-4"
                      checked={selected.includes(r.id)}
                      onChange={(e) => setSelected((s) =>
                        e.target.checked ? [...s, r.id] : s.filter((i) => i !== r.id))}
                    />
                  </td>
                )}
                <td className="px-3 py-2.5">
                  <div className="font-semibold text-slate-800">{r.name}</div>
                  <div className="text-[11px] text-slate-400 font-mono">{r.form_no}</div>
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-600">{r.matric_no}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600">{r.programme}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">{r.graduation}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500 whitespace-nowrap">{fmt(r.submitted_at)}</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  {r.status === "pending" ? (
                    <div className="flex items-center gap-1.5 justify-end">
                      <button
                        onClick={() => approve(r)}
                        disabled={deciding}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-40"
                      >
                        <ThumbsUp className="w-3.5 h-3.5" /> Confirm
                      </button>
                      <button
                        onClick={() => setDeclining(r)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-amber-300 text-amber-700 text-xs font-bold hover:bg-amber-50"
                      >
                        <Undo2 className="w-3.5 h-3.5" /> Decline
                      </button>
                    </div>
                  ) : (
                    <div className="text-right">
                      <span className={`text-[11px] font-bold rounded px-1.5 py-0.5 border ${
                        r.status === "approved"
                          ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                          : "text-amber-700 bg-amber-50 border-amber-200"
                      }`}>
                        {r.status === "approved" ? "Confirmed" : "Declined"}
                      </span>
                      <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1 justify-end">
                        <Clock className="w-3 h-3" /> {fmt(r.decided_at)}
                        {r.decided_by_secretariat && " · by the Secretariat"}
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {declining && (
        <DeclineDialog
          application={declining}
          onClose={() => setDeclining(null)}
          onDone={(m) => { setDeclining(null); setNote(m); }}
        />
      )}
    </div>
  );
}

/**
 * Declining one student.
 *
 * The reason is required and goes to the student word for word, so the box
 * says that plainly — a Head of Department writing "no" into a field they
 * think is internal is how a student ends up at the Secretariat asking what
 * happened.
 */
function DeclineDialog({
  application, onClose, onDone,
}: { application: HodApplication; onClose: () => void; onDone: (message: string) => void }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [decide, { isLoading }] = useDecideHodApplicationMutation();

  const go = async () => {
    setError("");
    if (!reason.trim()) return setError("Please give a reason — the student is shown it.");
    try {
      const res = await decide({ id: application.id, status: "declined", notes: reason.trim() }).unwrap();
      onDone(res?.message ?? "Declined.");
    } catch (e: any) {
      setError(e?.data?.message || "Could not save that.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md sm:rounded-xl">
        <div className="border-b px-5 py-3 flex items-center justify-between">
          <div>
            <div className="font-bold text-slate-800">Decline this application</div>
            <div className="text-xs text-slate-500">{application.name} · {application.matric_no}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-slate-100 text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-start gap-2.5 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
            <GraduationCap className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-900 leading-relaxed">
              The student is emailed this reason word for word. Say what is wrong, so they know
              what to do &mdash; &ldquo;not registered in this department this session&rdquo;
              rather than &ldquo;declined&rdquo;.
            </p>
          </div>

          <textarea
            rows={3}
            autoFocus
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200"
            placeholder="e.g. This matriculation number is not registered in this department."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />

          {error && <div className="text-xs text-red-600">{error}</div>}

          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border text-sm text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
            <button
              onClick={go}
              disabled={isLoading}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-bold hover:bg-amber-700 disabled:opacity-40"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
              Decline &amp; notify
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
